import { ApolloClient, ApolloLink, ApolloProvider, createHttpLink, InMemoryCache } from '@apollo/client';
import styled from '@emotion/styled';
import { CssBaseline } from '@material-ui/core';
import { ThemeProvider } from '@material-ui/core/styles';
import { History } from 'history';
import { parse } from 'qs';
import { ClassAttributes, Component, createElement } from 'react';
import { IntlProvider } from 'react-intl';
import { Router } from 'react-router';
import { RecoilRoot } from 'recoil';
import RecoilizeDebugger from 'recoilize';

import { SessionStorage } from '../adapter/BrowserStore';
import Config from '../core/Config';
import Dependency from '../core/Dependency';
import State from '../core/State';
import { jss, JssProvider } from '../customJss';
import GlobalStyles from '../GlobalStyles';
import theme from '../theme';
import { isIncludePath } from '../utility/isIncludePath';
import { HEADER_HEIGHT } from './component/compound/Header';
import ModalSpinner from './component/compound/ModalSpinner';
import { SIDEBAR_WIDTH } from './component/compound/SideBar';
import _SigningInIndicatorScreen from './component/compound/SigningInIndicatorScreen';
import DocumentTitle from './connected/DocumentTitle';
import _FatalErrorScreen from './connected/FatalErrorScreen';
import FormattedSnackbar from './connected/FormattedSnackbar';
import NonFatalErrorSnackbars from './connected/NonFatalErrorSnackbars';
import OverrideViewport from './connected/OverrideViewport';
import _SignedInScreen from './connected/SignedInScreen';
import _SignedOutScreen from './connected/SignedOutScreen';
import _ToolBar from './connected/Toolbar';
import { SPACING_REGULAR } from './constant/spacing';
import { Provider as ConfigProvider } from './context/config';
import { Provider as DependencyProvider } from './context/dependency';
import { Provider as HistoryProvider } from './context/history';
import { Provider as StateProvider } from './context/state';

interface Props {
  config: Config;
  dependency: Dependency;
  history: History;
  state: State;
}

type RootComponentProps = {
  noSidebar: boolean;
  needToolBar: boolean;
  isOpendByIframe: boolean;
};

type RootState = {
  isOpenedByIframe: boolean;
};

const config: Config = (window as any).__hugotconfig__;

const httpLink = createHttpLink({
  uri: config.HUGOT_API_GRAPHQL_ENDPOINT,
  credentials: 'include',
});

// INFO: GraphQLがCacheを使うために`__typename`をObjectに追加するが、それがMutation時にエラーを引き起こすので（Schemaに含まれていないので）
//   Linkとして毎回`__typename`を剥ぐように挙動をこちらから変更する。
// 参照: https://github.com/apollographql/apollo-client/issues/1564#issuecomment-357492659
const omitTypenameLink = new ApolloLink((operation, forward) => {
  if (operation.variables) {
    operation.variables = JSON.parse(JSON.stringify(operation.variables), (key, value) =>
      key === '__typename' ? undefined : value,
    );
  }

  return forward(operation);
});

// INFO: GraphQL Codegenで生成したApollo ClientのHookから読んだGraphQL Operationにデバッグ時ネットワークタブに名前が表示されるよう
//   URIにoperationNameを追加するLink。尚、〇〇WebApiのような旧設計のGraphQL Operationでは上記と同じようにURIにOperation Nameを
//   追加している。
const nameOperationLink = new ApolloLink((operation, forward) => {
  operation.setContext(() => ({
    uri: operation.operationName
      ? `${config.HUGOT_API_GRAPHQL_ENDPOINT}/${operation.operationName}`
      : config.HUGOT_API_GRAPHQL_ENDPOINT,
  }));

  return forward(operation);
});

const client = new ApolloClient({
  link: ApolloLink.from([nameOperationLink, omitTypenameLink, httpLink]),
  cache: new InMemoryCache(),
});

/**
 * responsiveで表示するpathのallow list.
 * sidebarかheaderかの出しわけにも必要なので,OverrideViewportから分離して親の階層でこのリストを管理する.
 */
const RESPONSIVE_LAYOUT_PATHS = [
  '/',
  '/latest-insights',
  '/direction_types',
  '/projects',
  '/projects/:projectId/deliverables',
  '/projects/:projectId/brief',
  '/projects/:projectId/reporting',
  '/search',
  '/settings',
  '/project_types',
  '/insight-categories',
];

class Root extends Component<ClassAttributes<HTMLElement> & Props, Record<string, unknown> & RootState> {
  constructor(props: Props) {
    super(props);

    this.state = {
      isResponsiveMode:
        window.innerWidth < 768 && isIncludePath(RESPONSIVE_LAYOUT_PATHS, this.props.history.location.pathname),
      isOpenedByIframe: (() => {
        let _isOpenedByIframe = false;
        if (new URLSearchParams(location.search).get('sidenav') === 'cloud') {
          SessionStorage.setItem('sidenav=cloud', 'true');
          _isOpenedByIframe = true;
        } else {
          SessionStorage.setItem('sidenav=cloud', 'false');
        }
        if (SessionStorage.getItem('sidenav=cloud') === 'true') {
          _isOpenedByIframe = true;
        }
        return _isOpenedByIframe;
      })(),
    };

    this.updateIsMobile = this.updateIsMobile.bind(this);

    this.props.history.listen(() => {
      this.updateIsMobile();
    });
  }

  updateIsMobile() {
    this.setState({
      isResponsiveMode:
        window.innerWidth < 768 && isIncludePath(RESPONSIVE_LAYOUT_PATHS, this.props.history.location.pathname),
    });
  }

  /**
   * mount時にwindow sizeの監視を登録
   */
  componentDidMount() {
    this.props.dependency.authenticationUsecase.signInFromPreviousSession();
    this.updateIsMobile();
    window.addEventListener('resize', this.updateIsMobile);

    // amazonAdAccountログイン後のページリダイレクトの場合、settingページのamazonAdAuthタブへ飛ばす(with tokenCode)
    if (location.href.match(/\/\?code=/) && location.href.match(/cpc_advertising/)) {
      const targetOrgId = window.localStorage.getItem('amazon-collector-target-orgId');
      const csrf = window.localStorage.getItem('amazon-collector-csrf');
      const query = parse(location.search.substring(1));

      window.localStorage.removeItem('amazon-collector-target-orgId');
      window.localStorage.removeItem('amazon-collector-csrf');

      try {
        if (csrf !== query.state) {
          // csrfが不一致の場合はページリダイレクトせず、エラーを表示する
          throw 'amazonCSRFError';
        } else {
          window.location.href = `/settings?amazonAdAuthCode=${query.code}&org=${targetOrgId}`;
        }
      } catch (_e) {
        this.props.dependency.errorPresenter.showAsNonFatal(new Error('message.amazon_create_token-invalid_params'));
      }
    } else if (location.href.match(/\/\?code=/)) {
      // yahoo連携リダイレクト後のURL例: https://hugot-local.pbcd.net:8000/?code=8bbc3b61126f5169&state=3526
      const targetOrgId = window.localStorage.getItem('yahoo-collector-target-orgId');
      const csrf = window.localStorage.getItem('yahoo-collector-csrf');
      const query = parse(location.search.substring(1));

      window.localStorage.removeItem('yahoo-collector-target-orgId');
      window.localStorage.removeItem('yahoo-collector-csrf');

      try {
        if (csrf !== query.state) {
          throw 'yahooCSRFError';
        } else {
          window.location.href = `/settings?yahooAdAuthCode=${query.code}&org=${targetOrgId}`;
        }
      } catch (_e) {
        this.props.dependency.errorPresenter.showAsNonFatal(new Error('message.yahoo_create_token-invalid_params'));
      }
    } else if (location.href.match(/\/\?auth_code=/)) {
      // TikTok連携リダイレクト後のURL例: https://hugot-local.pbcd.net:8000/?auth_code=8bbc3b61126f5169&state=3526
      const pathname = window.localStorage.getItem('tiktok-collector-pathname') || '';
      const targetOrgId = window.localStorage.getItem('tiktok-collector-target-orgId');
      const csrf = window.localStorage.getItem('tiktok-collector-csrf');
      const query = parse(location.search.substring(1));

      window.localStorage.removeItem('tiktok-collector-target-orgId');
      window.localStorage.removeItem('tiktok-collector-csrf');

      try {
        if (csrf !== query.state) {
          throw 'tiktokCSRFError';
        } else {
          window.location.href = `${pathname}?tiktokAdAuthCode=${query.auth_code}&org=${targetOrgId}`;
        }
      } catch (_e) {
        this.props.dependency.messageDispatcher.notifyUnimportant('message.tiktok_create_token-invalid_params');
      }
    } else {
      // yahoo連携を拒否した際の挙動
      if (location.href.match(/\/\?error=access_denied/)) {
        const targetOrgId = window.localStorage.getItem('yahoo-collector-target-orgId');
        const csrf = window.localStorage.getItem('yahoo-collector-csrf');
        const query = parse(location.search.substring(1));
        window.localStorage.removeItem('yahoo-collector-target-orgId');
        window.localStorage.removeItem('yahoo-collector-csrf');

        try {
          if (csrf !== query.state) {
            throw 'yahooCSRFError';
          } else {
            window.location.href = `/settings?org=${targetOrgId}`;
          }
        } catch (_e) {
          this.props.dependency.errorPresenter.showAsNonFatal(new Error('message.yahoo_create_token-invalid_params'));
        }
      }
    }
  }

  /**
   * unmount時にwindow sizeの監視を解除
   */
  componentWillUnmount() {
    window.removeEventListener('resize', this.updateIsMobile);
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.state.lastAccessedOrganization !== prevProps.state.lastAccessedOrganization) {
      if (
        this.props.state.lastAccessedOrganization &&
        this.props.state.lastAccessedOrganization.pointBalance !== null
      ) {
        this.props.dependency.acctUsecase.getProductPricing(this.props.state.lastAccessedOrganization.id);
      }
    }
  }

  componentDidCatch(err: Error) {
    this.props.dependency.errorPresenter.showAsFatal(err);
  }

  render() {
    // sidebarを出すべきかのflg.
    // sidebarの固定位置によってpaddingを挿入する位置が変わるのでこの情報が必要
    // * ログインページはサイドバーを出さない
    // * エラーページはサイドバーを出さない
    // * ResponsiveModeならサイドバーを出さない
    const shouldHideSideBar =
      !this.props.state.authentication.signedInUser || !!this.props.state.fatalError || this.state.isResponsiveMode;

    const noToolBar = !this.props.state.authentication.signedInUser || !!this.props.state.fatalError;

    const appRoot = document.getElementById('app');

    return (
      <ConfigProvider value={this.props.config}>
        <DependencyProvider value={this.props.dependency}>
          <HistoryProvider value={this.props.history}>
            <StateProvider value={this.props.state}>
              <Router history={this.props.history}>
                <ApolloProvider client={client}>
                  <RecoilRoot>
                    <RecoilizeDebugger root={appRoot} />
                    <JssProvider jss={jss}>
                      <ThemeProvider theme={theme}>
                        <CssBaseline />
                        <GlobalStyles />
                        <IntlProvider locale={this.props.state.language} messages={this.props.state.translations}>
                          <RootElement
                            needToolBar={!noToolBar}
                            noSidebar={!!shouldHideSideBar}
                            isOpendByIframe={this.state.isOpenedByIframe}
                          >
                            <DocumentTitle />
                            {/* FIXME: OverrideViewport の使用をやめる  */}
                            <OverrideViewport
                              shouldSetDeviceWidth={isIncludePath(
                                RESPONSIVE_LAYOUT_PATHS,
                                this.props.history.location.pathname,
                              )}
                            />
                            <SigningInIndicatorScreen
                              visible={this.props.state.authentication.fetching >= 1}
                              isOpendByIframe={this.state.isOpenedByIframe}
                            />
                            <SomethingProcessing
                              visible={this.props.state.fullScreenSpinner.necessity >= 1}
                              loadingMessage={this.props.state.fullScreenSpinner.loadingMessage}
                            />
                            <NonFatalErrorSnackbars />
                            <FormattedSnackbar />
                            <ToolBar
                              shouldHideSideBar={!!shouldHideSideBar}
                              isOpenedByIframe={this.state.isOpenedByIframe}
                            />
                            <FatalErrorScreen />
                            <SignedOutScreen />
                            <SignedInScreen />
                          </RootElement>
                        </IntlProvider>
                      </ThemeProvider>
                    </JssProvider>
                  </RecoilRoot>
                </ApolloProvider>
              </Router>
            </StateProvider>
          </HistoryProvider>
        </DependencyProvider>
      </ConfigProvider>
    );
  }
}

const RootElement = styled('div')<RootComponentProps>(
  {
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  ({ noSidebar, needToolBar, isOpendByIframe }) => ({
    paddingLeft: noSidebar ? 0 : isOpendByIframe ? 240 + SPACING_REGULAR : SIDEBAR_WIDTH + SPACING_REGULAR,
    paddingRight: noSidebar ? 0 : SPACING_REGULAR,
    paddingTop: noSidebar && needToolBar ? HEADER_HEIGHT : 0,
  }),
);

const ToolBar = styled(_ToolBar)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  width: SIDEBAR_WIDTH,
});

const FatalErrorScreen = styled(_FatalErrorScreen)({
  width: '100%',
  height: '100%',
});

const SignedOutScreen = styled(_SignedOutScreen)({
  width: '100%',
  height: '100%',
});

const SignedInScreen = styled(_SignedInScreen)({
  width: '100%',
  height: '100%',
});

const SomethingProcessing = styled(ModalSpinner)({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  zIndex: 10000 + 1,
});

const SigningInIndicatorScreen = styled(_SigningInIndicatorScreen)({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  zIndex: 10000,
});

export default Root;
